/**
 * Country Divergence Audit — Fabrizio CR 07/08/2026.
 *
 * Standalone admin report page listing every customer whose live
 * ``country`` field diverges from at least one signed contract's
 * ``customer_country`` snapshot. Complements the inline badge on
 * ``CustomersList.js`` — same data, but with sorting, filtering
 * and export-ready table format for Legal / Salesforce sync review.
 *
 * Two legitimate causes for a divergence (each requires a different
 * admin action):
 *   * Customer moved abroad AFTER signing → contract snapshot is
 *     legally correct; live ``country`` tracks the new residence.
 *     Action: GDPR Art. 16 addendum, no ``customers.country`` revert.
 *   * Data-entry mistake → snapshot / live values drifted without
 *     a real move. Action: revert ``customers.country`` to snapshot,
 *     no addendum needed.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Users, Calendar, ArrowRight, RefreshCw, Loader2 } from "lucide-react";
import { API } from "../config";

const COUNTRY_FLAGS = {
  IT: "🇮🇹", FR: "🇫🇷", ES: "🇪🇸", DE: "🇩🇪", UK: "🇬🇧", GB: "🇬🇧",
  PT: "🇵🇹", BE: "🇧🇪", NL: "🇳🇱", CH: "🇨🇭", AT: "🇦🇹", PL: "🇵🇱",
  SE: "🇸🇪", NO: "🇳🇴", FI: "🇫🇮", DK: "🇩🇰", IE: "🇮🇪", IRL: "🇮🇪",
  GR: "🇬🇷", LU: "🇱🇺", CZ: "🇨🇿", SK: "🇸🇰", HU: "🇭🇺", RO: "🇷🇴",
  BG: "🇧🇬", SI: "🇸🇮", HR: "🇭🇷", EE: "🇪🇪", LV: "🇱🇻", LT: "🇱🇹",
  CY: "🇨🇾", MT: "🇲🇹", GBZ: "🇬🇮",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const CountryDivergenceAuditPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const navigate = useNavigate();

  const fetchDivergences = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/audits/country-divergence?limit=1000`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDivergences(); }, []);

  const filtered = useMemo(() => {
    if (!filter.trim()) return items;
    const q = filter.trim().toLowerCase();
    return items.filter(i =>
      (i.customer_name || "").toLowerCase().includes(q) ||
      (i.customer_email || "").toLowerCase().includes(q) ||
      (i.current_country || "").toLowerCase().includes(q) ||
      (i.contract_country_snapshot || "").toLowerCase().includes(q)
    );
  }, [items, filter]);

  // Group by direction of change (which country moved where) so Legal
  // can spot patterns — "5 customers went IT → BE last month".
  const byMovement = useMemo(() => {
    const groups = {};
    items.forEach(i => {
      const key = `${i.contract_country_snapshot} → ${i.current_country}`;
      groups[key] = (groups[key] || 0) + 1;
    });
    return Object.entries(groups).sort((a, b) => b[1] - a[1]);
  }, [items]);

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in" data-testid="country-divergence-audit-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <AlertTriangle className="text-amber-400" size={32} />
          <div>
            <h1 className="text-2xl font-bold text-white">Audit — Divergenza paese</h1>
            <p className="text-sm text-gray-400 mt-1">
              Clienti con paese di residenza attuale diverso dallo snapshot al momento della firma.
            </p>
          </div>
        </div>
        <button
          onClick={fetchDivergences}
          className="btn-lojack-outline flex items-center gap-2"
          data-testid="refresh-divergence-btn"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          Aggiorna
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="wallet-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Totale divergenze</div>
          <div className="text-3xl font-bold text-amber-400" data-testid="divergence-total-count">{total}</div>
          <div className="text-xs text-gray-500 mt-1">contratti da rivedere</div>
        </div>
        <div className="wallet-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Clienti unici</div>
          <div className="text-3xl font-bold text-white">
            {new Set(items.map(i => i.customer_id)).size}
          </div>
          <div className="text-xs text-gray-500 mt-1">con almeno una divergenza</div>
        </div>
        <div className="wallet-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Movimento più comune</div>
          <div className="text-lg font-bold text-white truncate">
            {byMovement.length > 0 ? byMovement[0][0] : "—"}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {byMovement.length > 0 ? `${byMovement[0][1]} contratti` : "nessuna divergenza"}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="wallet-card p-4 mb-4">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtra per nome, email, paese…"
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-lojack-red focus:outline-none"
          data-testid="divergence-filter-input"
        />
        <div className="text-xs text-gray-500 mt-2">
          Mostrati {filtered.length} di {items.length} risultati totali
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="animate-spin text-lojack-red" size={40} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="wallet-card text-center py-12">
          <Users className="w-16 h-16 text-emerald-600/50 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nessuna divergenza rilevata</h3>
          <p className="text-gray-400">
            {filter ? "Nessun risultato per il filtro corrente." : "Tutti i contratti firmati hanno il paese cliente allineato."}
          </p>
        </div>
      ) : (
        <div className="wallet-card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Snapshot alla firma</th>
                  <th>Paese attuale</th>
                  <th>Firmato</th>
                  <th className="text-right">Giorni fa</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i, idx) => (
                  <tr key={`${i.customer_id}-${i.contract_id}`} data-testid={`divergence-row-${idx}`}>
                    <td>
                      <div className="font-medium text-white">
                        {i.customer_name || <span className="italic text-gray-500">(senza nome)</span>}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">{i.customer_email}</div>
                    </td>
                    <td>
                      <span className="text-xl mr-2">{COUNTRY_FLAGS[i.contract_country_snapshot] || "🏳️"}</span>
                      <span className="font-semibold text-white">{i.contract_country_snapshot}</span>
                    </td>
                    <td>
                      <div className="inline-flex items-center gap-2">
                        <ArrowRight size={14} className="text-amber-500" />
                        <span className="text-xl">{COUNTRY_FLAGS[i.current_country] || "🏳️"}</span>
                        <span className="font-semibold text-amber-300">{i.current_country}</span>
                      </div>
                    </td>
                    <td className="text-xs text-gray-400">
                      <Calendar size={12} className="inline mr-1" />
                      {fmtDate(i.signed_at)}
                    </td>
                    <td className="text-right font-mono text-gray-400">
                      {i.days_since_signing ?? "—"}
                    </td>
                    <td>
                      <button
                        onClick={() => navigate(`/admin/customers?highlight=${i.customer_id}`)}
                        className="text-xs px-3 py-1.5 bg-lojack-red/10 hover:bg-lojack-red/20 text-lojack-red border border-lojack-red/30 rounded-md transition"
                        data-testid={`open-customer-${i.customer_id}`}
                      >
                        Apri cliente →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryDivergenceAuditPage;
